// frontend/src/pages/Questions/QuestionDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, ArrowLeft, Heart, Bookmark, Share2, MessageCircle } from 'lucide-react';
import KaTeX from 'katex';
import 'katex/dist/katex.min.css';
import './QuestionDetail.css';

const QuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [userAnswer, setUserAnswer] = useState('');
  const [showSolution, setShowSolution] = useState(false);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);

  // Fetch question details
  const { data: question, isLoading, error } = useQuery({
    queryKey: ['question', id],
    queryFn: async () => {
      const response = await api.get(`/api/questions/${id}`);
      return response.data.question || response.data.data || response.data;
    }
  });

  // Submit answer mutation
  const submitAnswerMutation = useMutation({
    mutationFn: async (answer) => {
      const response = await api.post(`/api/questions/${id}/answer`, { answer });
      return response.data;
    },
    onSuccess: (data) => {
      setIsAnswerSubmitted(true);
      setIsCorrect(data.correct);
      setShowSolution(true);
      queryClient.invalidateQueries(['user', 'progress']);
    }
  });

  // Bookmark mutation
  const bookmarkMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/questions/${id}/bookmark`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['question', id]);
    }
  });

  // Handle answer submission
  const handleSubmitAnswer = () => {
    if (userAnswer.trim()) {
      submitAnswerMutation.mutate(userAnswer);
    }
  };

  // Render math content
  const renderMath = (content) => {
    if (!content) return null;
    
    try {
      // Replace LaTeX delimiters and render
      const processedContent = content
        .replace(/\$\$(.*?)\$\$/g, (match, tex) => {
          const html = KaTeX.renderToString(tex, { displayMode: true, throwOnError: false });
          return `<div class="math-display">${html}</div>`;
        })
        .replace(/\$(.*?)\$/g, (match, tex) => {
          const html = KaTeX.renderToString(tex, { displayMode: false, throwOnError: false });
          return `<span class="math-inline">${html}</span>`;
        });
      
      return <div dangerouslySetInnerHTML={{ __html: processedContent }} />;
    } catch (error) {
      console.error('Math rendering error:', error);
      return <div>{content}</div>;
    }
  };

  // Render question based on type
  const renderQuestionContent = () => {
    if (!question) return null;

    switch (question.solutionType) {
      case 'multipleChoice':
        return (
          <div className="multiple-choice-options">
            {question.options.map((option, index) => (
              <label key={index} className="option-label">
                <input
                  type="radio"
                  name="answer"
                  value={option.text}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  disabled={isAnswerSubmitted}
                />
                <span>{renderMath(option.latex || option.text)}</span>
              </label>
            ))}
          </div>
        );

      case 'numerical':
      case 'algebraic':
        return (
          <div className="text-answer">
            <input
              type="text"
              placeholder="Enter your answer"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              disabled={isAnswerSubmitted}
              className="answer-input"
            />
          </div>
        );

      case 'proof':
        return (
          <div className="proof-answer">
            <textarea
              placeholder="Write your proof here. Use $ for inline math and $$ for display math."
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              disabled={isAnswerSubmitted}
              className="proof-textarea"
              rows={10}
            />
          </div>
        );

      default:
        return (
          <div className="text-answer">
            <textarea
              placeholder="Enter your answer"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              disabled={isAnswerSubmitted}
              className="answer-textarea"
              rows={5}
            />
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="question-detail-loading">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="question-detail-error">
        <p>Error loading question. Please try again.</p>
        <button onClick={() => navigate(-1)} className="back-button">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="question-detail-container">
      <div className="question-detail-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={20} />
          Back
        </button>
        
        <div className="question-info">
          <span className="grade-badge">Grade {question?.grade}</span>
          <span className="topic-badge">{question?.topic}</span>
          <span className="marks-badge">{question?.marks} marks</span>
        </div>
      </div>

      <div className="question-detail-content">
        <div className="question-text">
          {renderMath(question?.question?.latex || question?.question?.text || question?.content || question?.question?.content)}
        </div>

        {question?.question?.images?.map((img, index) => (
          <img 
            key={index} 
            src={img} 
            alt={`Question figure ${index + 1}`} 
            className="question-image"
          />
        ))}

        <div className="answer-section">
          {renderQuestionContent()}
          
          {!isAnswerSubmitted && (
            <button 
              onClick={handleSubmitAnswer} 
              disabled={!userAnswer.trim() || submitAnswerMutation.isLoading}
              className="submit-button"
            >
              {submitAnswerMutation.isLoading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                'Submit Answer'
              )}
            </button>
          )}
        </div>

        {isAnswerSubmitted && (
          <div className="result-section">
            <div className={`result-message ${isCorrect ? 'correct' : 'incorrect'}`}>
              {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
            </div>
            
            {showSolution && question?.solution && (
              <div className="solution-section">
                <h3>Solution</h3>
                <div className="solution-answer">
                  <strong>Answer:</strong> {renderMath(question.solution.answer)}
                </div>
                
                {question.solution.steps && (
                  <div className="solution-steps">
                    <h4>Step-by-step solution:</h4>
                    {question.solution.steps.map((step, index) => (
                      <div key={index} className="solution-step">
                        <span className="step-number">Step {index + 1}:</span>
                        <div className="step-content">
                          {renderMath(step.latex || step.explanation)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="question-actions">
          <button 
            onClick={() => bookmarkMutation.mutate()}
            className={`action-button ${question?.isBookmarked ? 'active' : ''}`}
          >
            <Bookmark size={20} />
            <span>{question?.isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
          </button>
          
          <button className="action-button">
            <Share2 size={20} />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetail;